/**
 * CornerAdjust —— **影完之後手動拉四角**（掃描 App 式；透視拉平前一步）
 *
 * 為何要：App 內「自動搵文件邊」喺真實場景唔可靠（純 JS 啟發法試過唔得，
 * 官方 OpenCV.js 又搵唔到可靠 full build）。改為**影完俾用戶親手拉**——100% 可靠。
 * 預填：先試一次自動偵測；唔得就用「取景框」位置（＝唔拉都同框一致）。
 *
 * 座標：四角用**圖片歸一化 0–1**（同解像度無關）→ 呼叫者乘返 raster 寬高做 `warpQuad`。
 */
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

export type Pt01 = { x: number; y: number };

export default function CornerAdjust({
  url, initial, onConfirm, onCancel,
}: {
  url: string;
  initial: Pt01[];                        // [tl,tr,br,bl]（0–1）
  onConfirm: (corners: Pt01[]) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const dragging = useRef(-1);
  const [pts, setPts] = useState<Pt01[]>(initial);
  const [box, setBox] = useState({ left: 0, top: 0, width: 1, height: 1 });

  /** 量度「object-fit: contain」之下，圖片實際顯示區（相對 wrap 左上角） */
  const measure = () => {
    const img = imgRef.current, wrap = wrapRef.current;
    if (!img || !wrap || !img.naturalWidth || !img.naturalHeight) return;
    const wr = wrap.getBoundingClientRect();
    const scale = Math.min(wr.width / img.naturalWidth, wr.height / img.naturalHeight);
    const width = img.naturalWidth * scale, height = img.naturalHeight * scale;
    setBox({ left: (wr.width - width) / 2, top: (wr.height - height) / 2, width, height });
  };
  useEffect(() => {
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [url]);

  const moveTo = (i: number, clientX: number, clientY: number) => {
    const wrap = wrapRef.current;
    if (!wrap || !box.width) return;
    const wr = wrap.getBoundingClientRect();
    const x = Math.min(1, Math.max(0, (clientX - wr.left - box.left) / box.width));
    const y = Math.min(1, Math.max(0, (clientY - wr.top - box.top) / box.height));
    setPts((p) => p.map((q, j) => (j === i ? { x, y } : q)));
  };

  const px = (p: Pt01) => ({ x: box.left + p.x * box.width, y: box.top + p.y * box.height });
  const poly = pts.map((p) => { const q = px(p); return `${q.x},${q.y}`; }).join(' ');

  return (
    <div className="adjust-overlay">
      <p className="camera-hint">{t('capture.adjust_hint')}</p>
      <div className="adjust-stage" ref={wrapRef} onPointerMove={(e) => { if (dragging.current >= 0) moveTo(dragging.current, e.clientX, e.clientY); }}>
        <img ref={imgRef} className="adjust-img" src={url} alt="" onLoad={measure} />
        <svg className="adjust-line" width={box.width} height={box.height} style={{ left: box.left, top: box.top }}>
          <polygon points={poly} />
        </svg>
        {pts.map((p, i) => {
          const q = px(p);
          return (
            <button
              key={i} type="button" className="adjust-handle" style={{ left: q.x, top: q.y }}
              aria-label={`corner ${i + 1}`}
              onPointerDown={(e) => { dragging.current = i; e.currentTarget.setPointerCapture(e.pointerId); }}
              onPointerMove={(e) => { if (dragging.current === i) moveTo(i, e.clientX, e.clientY); }}
              onPointerUp={(e) => { dragging.current = -1; e.currentTarget.releasePointerCapture(e.pointerId); }}
            />
          );
        })}
      </div>
      <div className="adjust-actions">
        <button type="button" className="cta secondary" onClick={onCancel}>{t('capture.retake')}</button>
        <button type="button" className="cta" onClick={() => onConfirm(pts)}>{t('capture.apply')}</button>
      </div>
    </div>
  );
}
